import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Benvenuto, ${user.nome}!`);
      if (user.role === 'admin' || user.role === 'operator') {
        navigate('/backoffice');
      } else if (user.role === 'doctor') {
        navigate('/area-dottore');
      } else {
        navigate('/');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.requires_verification) {
        toast.info('Devi verificare la tua email prima di accedere');
        navigate('/verifica-email', { state: { email } });
      } else {
        setError(data?.detail || 'Credenziali non valide');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-[var(--shadow-md)]">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-[#238A92] rounded-xl flex items-center justify-center text-white mx-auto mb-2">
            <LogIn size={24} />
          </div>
          <CardTitle className="text-2xl" style={{fontFamily:'Space Grotesk'}}>Accedi</CardTitle>
          <CardDescription>Inserisci le tue credenziali per accedere</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle size={16} />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nome@esempio.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/password-dimenticata" className="text-xs text-[#238A92] hover:underline font-medium" data-testid="login-forgot-password-link">Password dimenticata?</Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="La tua password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="pr-10"
                />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37] transition-colors">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white" disabled={loading} data-testid="login-submit-button">
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Non hai un account?{' '}
              <Link to="/registrati" className="text-[#238A92] hover:underline font-medium">Registrati</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
